import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import {
  BankOutlined,
  ClearOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  FilterOutlined,
  FlagOutlined,
  Loading3QuartersOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import lib from '../../../assets/utils/lib';
import { useAuth } from '../../../assets/utils/useAuth';
import { states } from '../../../assets/utils/config';
import DebounceSelect from '../../../features/compoinents/DebouncedSelect';
import DashboardHero from '../../../features/dashboard/components/DashboardHero';
import ClaimsExportsDrawer from '../ClaimsExportsDrawer';
import { canCreateClaimsExport } from '../claimsExportHelpers';
import {
  ENCOUNTER_STATUS_OPTIONS,
  OUTPUT_FORMAT_OPTIONS,
  PERIOD_TYPE_OPTIONS,
  SOURCE_OPTIONS,
  buildFacilityEncounterSummaryParams,
  downloadFacilityEncounterSummarySync,
  getDefaultFilterValues,
  parseListResponse,
  assertFacilityEncounterSummaryResponse,
  validateFacilityEncounterSummaryFilters,
  previewFacilityEncounterSummary,
  queueFacilityEncounterSummaryExport,
} from './facilityEncounterSummaryHelpers';
import '../../../features/dashboard/index.css';
import './facility-encounter-summary.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const SORT_FIELD_MAP = {
  facility_name: 'facility_name',
  state: 'state',
  total_encounters: 'total_encounters',
  active_encounters: 'active_encounters',
  checked_out_encounters: 'checked_out_encounters',
  encounters_submitted_to_claims: 'encounters_submitted_to_claims',
  active_flags_count: 'active_flags_count',
  last_encounter_date: 'last_encounter_date',
};

function applySchemaDefaults(_schema, form) {
  form.setFieldsValue(getDefaultFilterValues());
}

function firstDefined(obj, keys) {
  for (const key of keys) {
    if (obj?.[key] != null) return Number(obj[key]) || 0;
  }
  return null;
}

function hasClaimsLadderAggregates(aggregates) {
  if (!aggregates || typeof aggregates !== 'object') return false;
  return (
    'encounters_pending_co' in aggregates
    || 'encounters_pending_hm' in aggregates
    || 'encounters_pending_cfo' in aggregates
    || 'encounters_paid' in aggregates
  );
}

export default function FacilityEncounterSummary({ embedded = false, onOpenExportsDrawer }) {
  const [form] = Form.useForm();
  const { user } = useAuth();

  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [aggregates, setAggregates] = useState(null);
  const [scope, setScope] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [sortBy, setSortBy] = useState('total_encounters');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(true);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [exportsDrawerOpen, setExportsDrawerOpen] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());
  const [listError, setListError] = useState(null);

  const openExportsDrawer = () => {
    if (embedded && onOpenExportsDrawer) {
      onOpenExportsDrawer();
    } else {
      setExportsDrawerOpen(true);
    }
  };

  const canExport = canCreateClaimsExport(user);
  const periodType = Form.useWatch('period_type', form);

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true);
    try {
      const resp = await lib.getFacilityEncounterSummarySchema();
      if (resp?.data?.error) throw new Error(resp?.data?.message || 'Failed to load schema');
      const data = resp?.data?.data || null;
      setSchema(data);
      applySchemaDefaults(data, form);
    } catch (e) {
      notification.error({
        message: 'Could not load report options',
        description: e?.message,
      });
      form.setFieldsValue(getDefaultFilterValues());
    } finally {
      setSchemaLoading(false);
    }
  }, [form]);

  const fetchList = useCallback(async (page = 1, pageSize = pagination.pageSize) => {
    setLoading(true);
    setListError(null);
    try {
      const values = form.getFieldsValue();
      const params = buildFacilityEncounterSummaryParams(values, {
        page,
        pageSize,
        sortBy,
        sortOrder,
      });
      const resp = await lib.getFacilityEncounterSummary(params);
      const body = assertFacilityEncounterSummaryResponse(resp);
      const parsed = parseListResponse(body);
      setFacilities(parsed.facilities);
      setAggregates(parsed.aggregates);
      setScope(parsed.scope);
      setPagination({
        current: parsed.pagination.page || page,
        pageSize: parsed.pagination.pageSize || pageSize,
        total: parsed.pagination.total || 0,
      });
      setLastRefreshed(new Date());
    } catch (e) {
      const message = e?.message || 'Could not load facility summary';
      setListError(message);
      notification.error({
        message: 'Could not load facility summary',
        description: message,
      });
      setFacilities([]);
      setAggregates(null);
    } finally {
      setLoading(false);
    }
  }, [form, pagination.pageSize, sortBy, sortOrder]);

  useEffect(() => {
    loadSchema();
  }, [loadSchema]);

  useEffect(() => {
    if (schemaLoading) return;
    fetchList(1);
  }, [schemaLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyFilters = () => {
    try {
      validateFacilityEncounterSummaryFilters(form.getFieldsValue());
      fetchList(1);
    } catch (e) {
      setListError(e?.message);
      notification.warning({
        message: 'Check your filters',
        description: e?.message,
      });
    }
  };

  const handlePeriodTypeChange = (nextType) => {
    const patch = { period_type: nextType };
    if (nextType === 'month') {
      patch.single_month = form.getFieldValue('single_month') || getDefaultFilterValues().single_month;
    } else if (nextType === 'date_range') {
      patch.date_range = form.getFieldValue('date_range') || [
        getDefaultFilterValues().single_month.startOf('month'),
        getDefaultFilterValues().single_month.endOf('month'),
      ];
    } else if (nextType === 'month_range') {
      const current = getDefaultFilterValues().single_month;
      patch.month_range = form.getFieldValue('month_range') || [
        current.subtract(2, 'month').startOf('month'),
        current.endOf('month'),
      ];
    }
    form.setFieldsValue(patch);
    setListError(null);
  };

  const handleResetFilters = () => {
    applySchemaDefaults(schema, form);
    setSortBy('total_encounters');
    setSortOrder('desc');
    fetchList(1);
  };

  const handleTableChange = (pager, _filters, sorter) => {
    const field = sorter?.field;
    const order = sorter?.order;
    let nextSortBy = sortBy;
    let nextSortOrder = sortOrder;
    if (field && SORT_FIELD_MAP[field]) {
      nextSortBy = SORT_FIELD_MAP[field];
      nextSortOrder = order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : 'desc';
      setSortBy(nextSortBy);
      setSortOrder(nextSortOrder);
    }
    const page = pager?.current || 1;
    const pageSize = pager?.pageSize || pagination.pageSize;
    setLoading(true);
    setListError(null);
    const values = form.getFieldsValue();
    let params;
    try {
      params = buildFacilityEncounterSummaryParams(values, {
        page,
        pageSize,
        sortBy: nextSortBy,
        sortOrder: nextSortOrder,
      });
    } catch (e) {
      setListError(e?.message);
      notification.warning({ message: 'Check your filters', description: e?.message });
      setLoading(false);
      return;
    }
    lib.getFacilityEncounterSummary(params)
      .then((resp) => {
        const body = assertFacilityEncounterSummaryResponse(resp);
        const parsed = parseListResponse(body);
        setFacilities(parsed.facilities);
        setAggregates(parsed.aggregates);
        setScope(parsed.scope);
        setPagination({
          current: parsed.pagination.page || page,
          pageSize: parsed.pagination.pageSize || pageSize,
          total: parsed.pagination.total || 0,
        });
      })
      .catch((e) => {
        const message = e?.message || 'Could not load facilities';
        setListError(message);
        notification.error({ message: 'Could not load facilities', description: message });
      })
      .finally(() => setLoading(false));
  };

  const buildExportParams = () => {
    const values = form.getFieldsValue();
    return buildFacilityEncounterSummaryParams(values, { sortBy, sortOrder });
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const data = await previewFacilityEncounterSummary(buildExportParams());
      setPreview(data);
      return data;
    } catch (e) {
      notification.error({ message: 'Preview failed', description: e?.message });
      return null;
    } finally {
      setPreviewLoading(false);
    }
  };

  const openExportModal = async () => {
    setExportModalOpen(true);
    await runPreview();
  };

  const handleSyncExport = async () => {
    setExportLoading('sync');
    try {
      const params = buildExportParams();
      await downloadFacilityEncounterSummarySync(params);
      notification.success({
        message: 'Download started',
        description: 'Your report file is downloading.',
      });
      setExportModalOpen(false);
    } catch (e) {
      if (e?.status === 413) {
        notification.warning({
          message: 'Report too large',
          description: 'Queue a background export instead.',
        });
      } else {
        notification.error({ message: 'Download failed', description: e?.message });
      }
    } finally {
      setExportLoading(null);
    }
  };

  const handleQueueExport = async () => {
    setExportLoading('queue');
    try {
      const params = buildExportParams();
      await queueFacilityEncounterSummaryExport(params);
      notification.success({
        message: 'Export queued',
        description: 'Open Claims exports to download when ready.',
      });
      setExportModalOpen(false);
      openExportsDrawer();
    } catch (e) {
      notification.error({ message: 'Export failed', description: e?.message });
    } finally {
      setExportLoading(null);
    }
  };

  const handleOrgSearch = async (search) => {
    const resp = await lib.orgs({ mode: 'mini', search });
    const body = resp?.data;
    if (body && !body.error) {
      return (body.data || []).map((org) => ({
        label: org.name,
        value: org.id,
      }));
    }
    return [];
  };

  const heroMetrics = useMemo(() => {
    if (!aggregates) return [];
    const a = aggregates;
    return [
      {
        key: 'facilities',
        title: 'Facilities',
        value: a.total_facilities,
        color: '#3497D3',
        icon: <BankOutlined />,
        subtitle: 'With encounters in scope',
      },
      {
        key: 'encounters',
        title: 'Total encounters',
        value: a.total_encounters,
        color: '#722ed1',
        icon: <UnorderedListOutlined />,
        subtitle: `${a.active_encounters ?? 0} active`,
      },
      {
        key: 'checked-out',
        title: 'Checked out',
        value: a.checked_out_encounters,
        color: '#595959',
        icon: <TeamOutlined />,
        subtitle: 'Closed encounters',
      },
      {
        key: 'submitted',
        title: 'Submitted to claims',
        value: a.encounters_submitted_to_claims,
        color: '#49B870',
        icon: <DownloadOutlined />,
        subtitle: 'Entered claims workflow',
      },
      {
        key: 'manual',
        title: 'Manual filings',
        value: a.manual_encounters,
        color: '#faad14',
        icon: <UnorderedListOutlined />,
        subtitle: `${a.direct_encounters ?? 0} direct`,
      },
      {
        key: 'escalation',
        title: 'With escalation',
        value: a.facilities_with_escalation,
        color: '#f5222d',
        icon: <FlagOutlined />,
        subtitle: 'Facilities flagged',
      },
    ].filter((m) => m.value != null);
  }, [aggregates]);

  const ladderMetrics = useMemo(() => {
    if (!hasClaimsLadderAggregates(aggregates)) return [];
    const metrics = [
      {
        key: 'pending_co',
        step: 1,
        title: 'Pending CO',
        value: firstDefined(aggregates, ['encounters_pending_co']),
        color: '#64748b',
        accent: '#94a3b8',
        icon: <ClockCircleOutlined />,
      },
      {
        key: 'pending_hm',
        step: 2,
        title: 'Pending HM',
        value: firstDefined(aggregates, ['encounters_pending_hm']),
        color: '#d97706',
        accent: '#fbbf24',
        icon: <Loading3QuartersOutlined />,
      },
      {
        key: 'pending_cfo',
        step: 3,
        title: 'Pending CFO',
        value: firstDefined(aggregates, ['encounters_pending_cfo']),
        color: '#c2410c',
        accent: '#fb923c',
        icon: <ExclamationCircleOutlined />,
      },
      {
        key: 'paid',
        step: 4,
        title: 'Paid',
        value: firstDefined(aggregates, ['encounters_paid']),
        color: '#15803d',
        accent: '#4ade80',
        icon: <CheckCircleOutlined />,
      },
    ];

    const total = aggregates.encounters_in_claims_ladder_total != null
      ? Number(aggregates.encounters_in_claims_ladder_total) || 0
      : metrics.reduce((sum, m) => sum + (m.value || 0), 0);

    return metrics.map((m) => ({
      ...m,
      share: total > 0 ? Math.round((m.value / total) * 100) : 0,
    }));
  }, [aggregates]);

  const ladderTotal = useMemo(() => {
    if (!hasClaimsLadderAggregates(aggregates)) return 0;
    if (aggregates.encounters_in_claims_ladder_total != null) {
      return Number(aggregates.encounters_in_claims_ladder_total) || 0;
    }
    return ladderMetrics.reduce((sum, m) => sum + (m.value || 0), 0);
  }, [aggregates, ladderMetrics]);

  const columns = [
    {
      title: 'Facility',
      dataIndex: 'facility_name',
      key: 'facility_name',
      sorter: true,
      sortOrder: sortBy === 'facility_name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (name, record) => (
        <div>
          <Text strong>{name || '—'}</Text>
          {record.facility_code ? (
            <div><Text type="secondary" style={{ fontSize: 11 }}>{record.facility_code}</Text></div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      sorter: true,
      sortOrder: sortBy === 'state' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      render: (v) => v || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      render: (v) => (v ? <span><MailOutlined style={{ marginRight: 4 }} />{v}</span> : '—'),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (v) => (v ? <span><PhoneOutlined style={{ marginRight: 4 }} />{v}</span> : '—'),
    },
    {
      title: 'Total',
      dataIndex: 'total_encounters',
      key: 'total_encounters',
      width: 90,
      sorter: true,
      sortOrder: sortBy === 'total_encounters' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: 'Active',
      dataIndex: 'active_encounters',
      key: 'active_encounters',
      width: 80,
      sorter: true,
      sortOrder: sortBy === 'active_encounters' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: 'Checked out',
      dataIndex: 'checked_out_encounters',
      key: 'checked_out_encounters',
      width: 100,
      sorter: true,
      sortOrder: sortBy === 'checked_out_encounters' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: 'Source',
      key: 'source',
      width: 120,
      render: (_, r) => (
        <Space size={4} wrap>
          <Tag color="gold">M {r.manual_encounters ?? 0}</Tag>
          <Tag color="cyan">D {r.direct_encounters ?? 0}</Tag>
        </Space>
      ),
    },
    {
      title: 'To claims',
      dataIndex: 'encounters_submitted_to_claims',
      key: 'encounters_submitted_to_claims',
      width: 90,
      sorter: true,
      sortOrder: sortBy === 'encounters_submitted_to_claims' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: 'Flags / escalation',
      dataIndex: 'escalation_summary',
      key: 'escalation_summary',
      ellipsis: true,
      render: (text, record) => {
        const summary = text || 'None';
        const isAlert = record.has_escalation;
        return (
          <span className={`fes-escalation${isAlert ? ' fes-escalation--alert' : ''}`}>
            {summary}
          </span>
        );
      },
    },
  ];

  const scopeLabel = useMemo(() => {
    if (!scope) return null;
    if (scope.period_type === 'all_time') return 'All time';
    if (scope.period_type === 'month' && scope.month) return scope.month;
    if (scope.month_from && scope.month_to) return `${scope.month_from} – ${scope.month_to}`;
    if (scope.date_from && scope.date_to) return `${scope.date_from} – ${scope.date_to}`;
    return null;
  }, [scope]);

  const syncAllowed = preview?.delivery?.sync_allowed !== false;

  const toolbarActions = (
    <>
      <button
        type="button"
        className={`dash-icon-btn ${showFilters ? 'is-active' : ''}`}
        onClick={() => setShowFilters((v) => !v)}
        aria-label="Toggle filters"
      >
        <FilterOutlined />
      </button>
      {!embedded && canExport ? (
        <Button
          type="default"
          size="small"
          icon={<ExportOutlined />}
          onClick={openExportsDrawer}
        >
          Exports
        </Button>
      ) : null}
      {canExport ? (
        <Button
          type="primary"
          size="small"
          icon={<DownloadOutlined />}
          onClick={openExportModal}
          disabled={schemaLoading}
        >
          Export
        </Button>
      ) : null}
      <Button
        type="default"
        size="small"
        icon={<ReloadOutlined />}
        onClick={() => fetchList(pagination.current)}
        loading={loading}
      >
        Refresh
      </Button>
    </>
  );

  return (
    <div className={`facility-encounter-summary${embedded ? ' facility-encounter-summary--embedded' : ''}`}>
      <div className={embedded ? undefined : 'dashboard-page-stack'}>
        {embedded ? (
          <div className="fes-toolbar fes-toolbar--embedded">
            <div className="fes-toolbar__scope">
              {scopeLabel ? <Text type="secondary">Scope: {scopeLabel}</Text> : null}
              <Text type="secondary" className="fes-toolbar__updated">
                Updated {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
            </div>
            <div className="fes-toolbar__actions">{toolbarActions}</div>
          </div>
        ) : (
          <header className="dash-masthead">
            <div className="dash-masthead__lead">
              <h1 className="dash-masthead__title">Facility summary</h1>
              <p className="dash-masthead__meta">
                {scopeLabel ? <span>{scopeLabel}</span> : null}
                {scopeLabel ? <span className="dash-masthead__sep">·</span> : null}
                <span>Updated {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </p>
            </div>
            <div className="dash-masthead__actions">{toolbarActions}</div>
          </header>
        )}

        {showFilters ? (
          <div className="dash-filters">
            <Form form={form} layout="vertical" onFinish={handleApplyFilters}>
              <Row gutter={[12, 8]} align="bottom">
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="period_type" label="Period" className="fes-filter-label" style={{ marginBottom: 8 }}>
                    <Select options={PERIOD_TYPE_OPTIONS} onChange={handlePeriodTypeChange} />
                  </Form.Item>
                </Col>
                {periodType === 'date_range' ? (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="date_range" label="Date range" style={{ marginBottom: 8 }}>
                      <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                ) : null}
                {periodType === 'month' ? (
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item name="single_month" label="Month" style={{ marginBottom: 8 }}>
                      <DatePicker picker="month" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                ) : null}
                {periodType === 'month_range' ? (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="month_range" label="Month range" style={{ marginBottom: 8 }}>
                      <RangePicker picker="month" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                ) : null}
                <Col xs={12} sm={8} md={4}>
                  <Form.Item name="date_type" label="Date basis" style={{ marginBottom: 8 }}>
                    <Select
                      options={[
                        { value: 'checkin_date', label: 'Check-in date' },
                        { value: 'checkout_date', label: 'Check-out date' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Form.Item name="source" label="Filing source" style={{ marginBottom: 8 }}>
                    <Select options={SOURCE_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Form.Item name="encounter_status" label="Encounter status" style={{ marginBottom: 8 }}>
                    <Select options={ENCOUNTER_STATUS_OPTIONS} />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Form.Item name="state" label="State" style={{ marginBottom: 8 }}>
                    <Select allowClear placeholder="All states" options={states} showSearch optionFilterProp="label" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Form.Item name="has_escalation" label="Escalation" style={{ marginBottom: 8 }}>
                    <Select
                      allowClear
                      placeholder="All"
                      options={[
                        { value: true, label: 'Has escalation' },
                        { value: false, label: 'No escalation' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="organization_id" label="Organization" style={{ marginBottom: 8 }}>
                    <DebounceSelect
                      allowClear
                      showSearch
                      placeholder="All organizations"
                      fetchOptions={handleOrgSearch}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="search" label="Search facility" style={{ marginBottom: 8 }}>
                    <Input allowClear placeholder="Name, code, city…" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Space wrap>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Apply filters
                    </Button>
                    <Button icon={<ClearOutlined />} onClick={handleResetFilters}>
                      Reset
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </div>
        ) : null}

        {listError ? (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setListError(null)}
            style={{ marginBottom: 16 }}
            message="Could not load facility summary"
            description={listError}
          />
        ) : null}

        {schema?.submitted_to_claims_definition ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Submitted to claims: ${schema.submitted_to_claims_definition.replace(/_/g, ' ')}`}
          />
        ) : null}

        <DashboardHero metrics={heroMetrics} loading={loading && !aggregates} />

        {hasClaimsLadderAggregates(aggregates) ? (
          <section className="fes-ladder" aria-label="Claims pipeline by stage">
            <div className="fes-ladder__masthead">
              <div>
                <h2 className="fes-ladder__title">Claims pipeline</h2>
                <p className="fes-ladder__subtitle">
                  {ladderTotal.toLocaleString()} encounters across payment stages
                </p>
              </div>
              <div className="fes-ladder__legend">
                {ladderMetrics.map((metric) => (
                  <span key={metric.key} className="fes-ladder__legend-item">
                    <span
                      className="fes-ladder__legend-dot"
                      style={{ background: metric.accent }}
                    />
                    {metric.title}
                  </span>
                ))}
              </div>
            </div>
            <div className="fes-ladder__track">
              {ladderMetrics.map((metric, index) => (
                <React.Fragment key={metric.key}>
                  {index > 0 ? <div className="fes-ladder__connector" aria-hidden="true" /> : null}
                  <article
                    className={`fes-ladder__stage fes-ladder__stage--${metric.key}`}
                    style={{ '--stage-color': metric.color, '--stage-accent': metric.accent }}
                  >
                    <div className="fes-ladder__stage-glow" aria-hidden="true" />
                    <div className="fes-ladder__stage-watermark" aria-hidden="true">
                      {metric.icon}
                    </div>
                    <div className="fes-ladder__stage-top">
                      <span className="fes-ladder__step">Step {metric.step}</span>
                      <span className="fes-ladder__stage-icon">{metric.icon}</span>
                    </div>
                    <div className="fes-ladder__stage-label">{metric.title}</div>
                    <div className="fes-ladder__stage-value">
                      {Number(metric.value).toLocaleString()}
                    </div>
                    <div className="fes-ladder__stage-foot">
                      <span className="fes-ladder__stage-share">{metric.share}% of pipeline</span>
                      <div className="fes-ladder__stage-bar" aria-hidden="true">
                        <span style={{ width: `${metric.share}%` }} />
                      </div>
                    </div>
                  </article>
                </React.Fragment>
              ))}
            </div>
          </section>
        ) : null}

        <Table
          rowKey={(r) => r.facility_id}
          columns={columns}
          dataSource={facilities}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} facilities`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1100 }}
          size="middle"
        />
      </div>

      <Modal
        title="Export facility summary"
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setExportModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="preview" loading={previewLoading} onClick={runPreview}>
            Refresh preview
          </Button>,
          syncAllowed ? (
            <Button
              key="sync"
              type="default"
              icon={<DownloadOutlined />}
              loading={exportLoading === 'sync'}
              onClick={handleSyncExport}
            >
              Download now
            </Button>
          ) : null,
          <Button
            key="queue"
            type="primary"
            icon={<ExportOutlined />}
            loading={exportLoading === 'queue'}
            onClick={handleQueueExport}
          >
            {syncAllowed ? 'Queue export' : 'Queue background export'}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="output_format" label="File format">
            <Select options={OUTPUT_FORMAT_OPTIONS} />
          </Form.Item>
        </Form>
        {previewLoading && !preview ? (
          <Text type="secondary">Checking export size…</Text>
        ) : null}
        {preview?.summary ? (
          <div style={{ marginTop: 12 }}>
            <Text>
              <strong>{preview.summary.total_facilities ?? 0}</strong> facilities ·{' '}
              <strong>{preview.summary.total_encounters ?? 0}</strong> encounters ·{' '}
              <strong>{preview.summary.encounters_submitted_to_claims ?? 0}</strong> submitted to claims
            </Text>
            {preview.delivery?.recommended === 'async' ? (
              <div className="fes-export-hint">
                Large scope — background export recommended
                {preview.delivery?.reason ? ` (${preview.delivery.reason})` : ''}.
              </div>
            ) : (
              <div className="fes-export-hint">You can download immediately or queue for later.</div>
            )}
          </div>
        ) : null}
      </Modal>

      {!embedded ? (
        <ClaimsExportsDrawer open={exportsDrawerOpen} onClose={() => setExportsDrawerOpen(false)} />
      ) : null}
    </div>
  );
}
